import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent,
} from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ImageIcon, X, Calendar } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/gallery").then(r => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const categories = ["All", ...Array.from(new Set(items.map(i => i.category).filter(Boolean)))];
  const filtered = filter === "All" ? items : items.filter(i => i.category === filter);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" /> Gallery
              </div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Moments from KELTA</h1>
              <p className="text-muted-foreground mt-2 max-w-2xl">
                A visual archive of meetings, recognitions, training programs and milestones
                of the Kerala Local Body Technical Assistants Association.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(c => (
                <Button
                  key={c}
                  size="sm"
                  variant={filter === c ? "default" : "outline"}
                  className={filter === c ? "bg-brand-blue text-white" : ""}
                  onClick={() => setFilter(c)}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </Button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <Card className="p-12 text-center text-muted-foreground">
              No photos yet in this category.
            </Card>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
              {filtered.map((g, i) => (
                <motion.div
                  key={g.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 9) * 0.04 }}
                  className="mb-4 break-inside-avoid group cursor-pointer rounded-xl overflow-hidden border border-border bg-card hover-lift"
                  onClick={() => setActive(g)}
                  data-testid={`gallery-item-${g.id}`}
                >
                  <div className="relative">
                    <img
                      src={g.image_url}
                      alt={g.caption || "KELTA gallery"}
                      loading="lazy"
                      className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {(g.caption || g.category) && (
                    <div className="p-3">
                      {g.category && (
                        <Badge variant="secondary" className="text-[10px]">{g.category}</Badge>
                      )}
                      {g.caption && (
                        <p className="text-sm mt-1.5 leading-snug line-clamp-2">{g.caption}</p>
                      )}
                      {g.date && (
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {g.date}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
      <Footer />

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black/95 border-none">
          {active && (
            <div className="relative">
              <button
                onClick={() => setActive(null)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Close"
                data-testid="gallery-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
              <img
                src={active.image_url}
                alt={active.caption || "KELTA gallery"}
                className="w-full max-h-[85vh] object-contain"
              />
              {(active.caption || active.category) && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-5 text-white">
                  {active.category && <Badge className="text-[10px] bg-brand-orange/90 border-none">{active.category}</Badge>}
                  {active.caption && <p className="text-sm mt-2 leading-relaxed max-w-3xl">{active.caption}</p>}
                  {active.date && <div className="text-xs text-white/70 mt-1">{active.date}</div>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
